import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { EvidenceInfo } from '../../PPWB/dtos/evidenceInfo.dto';
import { PromptType } from '../types/taskTypes';

export interface ISummaryResponse {
  summary?: string;
  promptId?: number;
  like?: boolean;
  reason?: string;
  timeSaved?: number;
  // Mark as dirty when any text field is updated
  // Enable the save button only when the "isDirty" flag is true
  isDirty?: boolean;
}

export interface ISummary extends ISummaryResponse, IEvidenceInfo {}

interface IEvidenceInfo extends EvidenceInfo {
  promptType: PromptType;
  evidenceId: number;
}

export interface IEvidencesSummary extends ISummaryResponse {
  summaries: IEvidenceInfo[];
  promptType: PromptType;
}

export type SummaryUnion = ISummary | IEvidencesSummary;

const getKey = (promptType: PromptType, evidenceId?: number) =>
  evidenceId
    ? JSON.stringify({ evidenceId, promptType })
    : promptType.toString();

const summaryResponseAdapter = createEntityAdapter<SummaryUnion>({
  selectId: summary =>
    getKey(
      summary.promptType,
      'evidenceId' in summary ? summary.evidenceId : undefined,
    ),
});

const summaryResponseSlice = createSlice({
  name: 'summaryResponse',
  initialState: summaryResponseAdapter.getInitialState(),
  reducers: {
    setAll: summaryResponseAdapter.setAll,
    addOne: summaryResponseAdapter.addOne,
    addManySummaryResponses: summaryResponseAdapter.addMany,
    updateSummary: (state, action: PayloadAction<ISummary>) => {
      const {
        evidenceId,
        like,
        promptType,
        reason,
        timeSaved,
        promptId,
        summary: aiSummary,
      } = action.payload;
      const key = getKey(promptType, evidenceId);
      const summary = state.entities[key];
      if (summary) {
        summary.like = like;
        summary.reason = reason;
        summary.timeSaved = timeSaved;
        summary.summary = aiSummary;
        summary.promptId = promptId;
      }
    },
    updateLike: (state, action: PayloadAction<Partial<ISummary>>) => {
      const { evidenceId, like, promptType } = action.payload;
      const key = getKey(promptType!, evidenceId);
      const summary = state.entities[key];
      if (summary) {
        summary.like = like;
        summary.isDirty = true;
      }
    },
    updateReason: (state, action: PayloadAction<Partial<ISummary>>) => {
      const { evidenceId, reason, promptType } = action.payload;
      const key = getKey(promptType!, evidenceId);
      const summary = state.entities[key];
      if (summary) {
        summary.reason = reason;
        summary.isDirty = true;
      }
    },
    updateTimeSaved: (state, action: PayloadAction<Partial<ISummary>>) => {
      const { evidenceId, timeSaved, promptType } = action.payload;
      const key = getKey(promptType!, evidenceId);
      const summary = state.entities[key];
      if (summary) {
        summary.timeSaved = timeSaved;
        summary.isDirty = true;
      }
    },
  },
});

export const summaryListActions = summaryResponseSlice.actions;
export const summaryResponseReducer = summaryResponseSlice.reducer;
export const { selectAll: getAllSummaries } =
  summaryResponseAdapter.getSelectors();
